export interface Rsvp {
  /**
   * RSVP ID.
   * @format GUID
   */
  id?: string;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * Contact ID associated with this RSVP.
   * @format GUID
   */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string;
  /** RSVP created timestamp. */
  created?: Date | null;
  /** RSVP modified timestamp. */
  modified?: Date | null;
  /** First name. */
  firstName?: string;
  /** Last name. */
  lastName?: string;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string;
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /** RSVP response status. */
  status?: RsvpStatusWithLiterals;
  /** Total number of attendees. */
  totalGuests?: number;
  /** List of guests. */
  guests?: Guest[];
  /** Whether RSVP is anonymized by GDPR delete. */
  anonymized?: boolean;
  /** Whether marketing consent was given */
  marketingConsent?: boolean | null;
}

export interface FormResponse {
  /**
   * Form field inputs.
   * @maxSize 200
   */
  inputValues?: InputValue[];
}

export interface InputValue {
  /**
   * Form field input name.
   * @maxLength 100
   */
  inputName?: string;
  /**
   * Form field value.
   * @maxLength 5000
   */
  value?: string;
  /**
   * Form field values.
   * @maxSize 100
   * @maxLength 5000
   */
  values?: string[];
}

export interface FormattedAddress {
  /**
   * 1-line address representation.
   * @maxLength 200
   */
  formatted?: string;
  /** Address components. */
  address?: Address;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision shorthand. Usually, a short code (2 or 3 letters) that represents a state, region, prefecture, or province. e.g. NY */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Short subdivision code. */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

/** Subdivision Concordance values */
export interface StandardDetails {
  /**
   * subdivision iso-3166-2 code according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). e.g. US-NY, GB-SCT, NO-30
   * @maxLength 20
   */
  iso31662?: string | null;
}

export enum RsvpStatus {
  YES = 'YES',
  NO = 'NO',
  WAITING = 'WAITING',
}

/** @enumType */
export type RsvpStatusWithLiterals = RsvpStatus | 'YES' | 'NO' | 'WAITING';

export interface Guest {
  /** Index in the RSVP guest list. */
  index?: number;
  /** Guest full name. */
  fullName?: string;
  /** Guest check-in. */
  checkIn?: CheckIn;
  /**
   * Unique guest ID per RSVP.
   * @min 1
   */
  id?: number;
}

export interface CheckIn {
  /** Time of a ticket's check-in. */
  created?: Date | null;
}

export interface ListRsvpRequest {
  /** Number of items to skip. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination). */
  offset?: number;
  /**
   * Number of items to load. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   * @max 400
   */
  limit?: number;
  /**
   * Controls which data is returned. See [Fieldset](https://dev.wix.com/api/rest/wix-events/wix-events/fieldset#wix-events_wix-events_fieldset_rsvp-fieldset).
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
  /**
   * Event ID.
   * @format GUID
   * @maxSize 100
   */
  eventId?: string[];
  /**
   * RSVP ID.
   * @format GUID
   * @maxSize 500
   */
  rsvpId?: string[];
  /**
   * RSVP status.
   * @maxSize 20
   */
  status?: RsvpStatusWithLiterals[];
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Facet counts to include in the response.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Textual search filter - search is performed on "full_name" and "email".
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator id filter, by default any.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order, defaults to `"created:asc"`.
   * @maxLength 100
   */
  sort?: string;
  /**
   * Contact ID.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
  /** RSVP tag */
  tag?: RsvpTagWithLiterals[];
}

export enum RsvpFieldset {
  /** Include RSVP details including: `created`, `modified`, `firstName`, `lastName`, `status`, `totalGuests`, `guests`, and `annonymized`. */
  DETAILS = 'DETAILS',
  /** Include RSVP form. */
  FORM = 'FORM',
  /** Include RSVP email. */
  CONTACT_DETAILS = 'CONTACT_DETAILS',
}

/** @enumType */
export type RsvpFieldsetWithLiterals =
  | RsvpFieldset
  | 'DETAILS'
  | 'FORM'
  | 'CONTACT_DETAILS';

export enum RsvpTag {
  /** Return only RSVPs of all guests that are fully checked-in. */
  FULLY_CHECKED_IN = 'FULLY_CHECKED_IN',
  /** Return only RSVPs of all guests that aren't fully checked-in. */
  NOT_FULLY_CHECKED_IN = 'NOT_FULLY_CHECKED_IN',
  /** Return only RSVPs of guests that are members. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type RsvpTagWithLiterals =
  | RsvpTag
  | 'FULLY_CHECKED_IN'
  | 'NOT_FULLY_CHECKED_IN'
  | 'MEMBER';

export interface ListRsvpResponse {
  /** Total RSVPs matching the given filters. */
  total?: number;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 100
   */
  limit?: number;
  /** RSVP list. */
  rsvps?: Rsvp[];
  /** Facet query result. */
  facets?: Record<string, FacetCounts>;
  /** Rsvp data enriched facets. */
  rsvpFacets?: RsvpFacets;
}

export interface FacetCounts {
  /** Facet counts aggregated per value. */
  counts?: Record<string, number>;
}

export interface RsvpFacets {
  /** Filter facets. */
  facets?: Record<string, RsvpFacetCounts>;
}

export interface RsvpFacetCounts {
  /** Facet totals, aggregated per filter. */
  counts?: Record<string, Counts>;
}

export interface Counts {
  /** Number of RSVPs. */
  count?: number;
  /** Number of guests within RSVPs. */
  guests?: number;
  /** Number of guests who have checked-in. */
  guestsCheckIn?: number;
}

export interface QueryRsvpRequest {
  /** Offset. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination). */
  offset?: number;
  /**
   * Limit. See [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   * @max 100
   */
  limit?: number;
  /**
   * Control which data is returned. See [Fieldset](https://dev.wix.com/api/rest/wix-events/wix-events/fieldset#wix-events_wix-events_fieldset_rsvp-fieldset).
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
  /** Filter. */
  filter?: Record<string, any> | null;
  /**
   * Site member ID.
   * @format GUID
   * @maxSize 500
   */
  memberId?: string[];
  /**
   * Filter facets to include in the response.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Textual search filter - search is performed on "guests.full_name".
   * @maxLength 200
   */
  searchPhrase?: string;
  /**
   * Event creator ID.
   * @format GUID
   * @maxSize 50
   */
  eventCreatorId?: string[];
  /**
   * Sort order, defaults to `"created:asc"`.
   * @maxLength 100
   */
  sort?: string;
  /**
   * Contact ID.
   * @format GUID
   * @maxSize 100
   */
  contactId?: string[];
  /** RSVP tag */
  tag?: RsvpTagWithLiterals[];
}

export interface QueryRsvpResponse {
  /** Total RSVPs matching the given filters. */
  total?: number;
  /** Offset. */
  offset?: number;
  /**
   * Limit.
   * @max 100
   */
  limit?: number;
  /** RSVP list. */
  rsvps?: Rsvp[];
  /** Facet query result. */
  facets?: Record<string, FacetCounts>;
  /** Rsvp data enriched facets. */
  rsvpFacets?: RsvpFacets;
}

export interface GetRsvpRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Controls which data is returned. See [Fieldset](https://dev.wix.com/api/rest/wix-events/wix-events/fieldset#wix-events_wix-events_fieldset_rsvp-fieldset).
   * @maxSize 20
   */
  fieldset?: RsvpFieldsetWithLiterals[];
}

export interface GetRsvpResponse {
  /** RSVP. */
  rsvp?: Rsvp;
}

export interface CreateRsvpRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** RSVP form response. */
  form?: FormResponse;
  /** RSVP status. */
  status?: RsvpStatusWithLiterals;
  /**
   * Member ID of the RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /**
   * Create RSVP options.
   * WIX_EVENTS.MANAGE_RSVP permission is required.
   */
  options?: ModificationOptions;
  /** Whether marketing consent was given */
  marketingConsent?: boolean | null;
}

export interface ModificationOptions {
  /** Whether to ignore notification settings (when hen true, no notifications to contact or user are sent). */
  silent?: boolean;
  /** Whether to create/update regardless of event guest limit. */
  ignoreLimits?: boolean;
  /** Whether to ignore the form validation. */
  ignoreFormValidation?: boolean;
}

export interface CreateRsvpResponse {
  /** Created RSVP. */
  rsvp?: Rsvp;
  /** "Add to calendar" links. */
  calendarLinks?: CalendarLinks;
}

export interface CalendarLinks {
  /** "Add to Google calendar" URL. */
  google?: string;
  /** "Download ICS calendar file" URL. */
  ics?: string;
}

export interface RsvpCreated {
  /** RSVP created timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Site language when RSVP created
   * @format LANGUAGE
   */
  language?: string | null;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId?: string;
  /** Contact ID associated with this RSVP. */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /** Guest first name. */
  firstName?: string;
  /** Guest last name. */
  lastName?: string;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string;
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /** RSVP response status. */
  status?: RsvpStatusWithLiterals;
  /** List of all guests. */
  guests?: Guest[];
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
}

export interface OnlineConferencingLogin {
  /**
   * Link URL to the online conference.
   * @format WEB_URL
   * @readonly
   */
  link?: string;
  /**
   * Password for the online conference.
   * @readonly
   */
  password?: string | null;
}

export interface UpdateRsvpRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId: string;
  /** Set of field paths, specifying which parts of RSVP to update. */
  fields: string[];
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /** RSVP response status. */
  status?: RsvpStatusWithLiterals;
  /**
   * Update RSVP options.
   * WIX_EVENTS.MANAGE_RSVP permission is required.
   */
  options?: ModificationOptions;
}

export interface UpdateRsvpResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface RsvpUpdated {
  /** RSVP updated timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Site language when RSVP created
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Locale in which Rsvp was created.
   * @format LANGUAGE_TAG
   */
  locale?: string | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId?: string;
  /** Contact ID associated with this RSVP. */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /** RSVP created timestamp. */
  created?: Date | null;
  /** Guest first name. */
  firstName?: string;
  /** Guest last name. */
  lastName?: string;
  /**
   * Guest email.
   * @format EMAIL
   */
  email?: string;
  /** RSVP form response. */
  rsvpForm?: FormResponse;
  /** RSVP response status. */
  status?: RsvpStatusWithLiterals;
  /** List of the guests. */
  guests?: Guest[];
  /** URL and password to online conference */
  onlineConferencingLogin?: OnlineConferencingLogin;
  /** Notifications silenced for this domain event. */
  silent?: boolean | null;
}

export interface BulkUpdateRsvpRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVPs to update.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  rsvpId?: string[];
  /** Set of fields to update. */
  fields?: string[];
  /** New RSVP status. */
  status?: RsvpStatusWithLiterals;
}

export interface BulkUpdateRsvpResponse {
  /** Updated RSVPs. */
  rsvps?: Rsvp[];
}

export interface DeleteRsvpRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVPs to delete.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  rsvps?: string[];
}

export interface DeleteRsvpResponse {}

export interface RsvpDeleted {
  /** RSVP deleted timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * RSVP ID.
   * @format GUID
   */
  rsvpId?: string;
  /** Contact ID associated with this RSVP. */
  contactId?: string;
  /**
   * Member ID associated with this RSVP.
   * @format GUID
   */
  memberId?: string | null;
  /** Whether RSVP was anonymized by GDPR delete. */
  anonymized?: boolean;
}

export interface CheckInRsvpRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVP ID to check-in.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Guest IDs to check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestId?: number[];
}

export interface CheckInRsvpResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface DeleteRsvpCheckInRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * RSVP ID to delete check-in.
   * @format GUID
   */
  rsvpId: string;
  /**
   * Guest IDs to delete check-in.
   * @min 1
   * @minSize 1
   * @maxSize 11
   */
  guestId?: number[];
}

export interface DeleteRsvpCheckInResponse {
  /** Updated RSVP. */
  rsvp?: Rsvp;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
